// -----------------------------------------------------------
// FloraIQ - Versioned Events (Aba 7: Histórico e Versionamento)
// -----------------------------------------------------------

import { type FC, useState } from "react";
import { ladmEvents, versionedObjectExample } from "@/mocks/ladm";

const eventIcons: Record<string, string> = {
  "Cadastro inicial": "ri-add-circle-line",
  "Vistoria técnica": "ri-clipboard-line",
  "Vistoria fitossanitária em lote": "ri-clipboard-line",
  "Alteração de risco": "ri-error-warning-line",
  "Alteração de status": "ri-edit-line",
  "Poda corretiva realizada": "ri-scissors-cut-line",
  "Poda de limpeza": "ri-scissors-line",
  "Tratamento fitossanitário": "ri-medicine-bottle-line",
  "Atualização por laser scanner": "ri-scan-2-line",
  "Atualização por drone": "ri-flight-takeoff-line",
  "Plantio de muda compensatória": "ri-plant-line",
  "Queda de árvore": "ri-close-circle-line",
  "Replantio": "ri-seedling-line",
  "Solicitação de vistoria pelo cidadão": "ri-user-voice-line",
  "Dano por tempestade": "ri-thunderstorms-line",
  "Supressão autorizada": "ri-check-double-line",
  "Interferência registrada": "ri-alert-line",
  "Transplante autorizado": "ri-arrow-left-right-line",
};

const eventColors: Record<string, string> = {
  "Cadastro inicial": "bg-blue-100 text-blue-600",
  "Vistoria técnica": "bg-green-100 text-green-600",
  "Vistoria fitossanitária em lote": "bg-green-100 text-green-600",
  "Alteração de risco": "bg-orange-100 text-orange-600",
  "Alteração de status": "bg-yellow-100 text-yellow-600",
  "Poda corretiva realizada": "bg-blue-100 text-blue-600",
  "Poda de limpeza": "bg-blue-100 text-blue-600",
  "Tratamento fitossanitário": "bg-green-100 text-green-600",
  "Atualização por laser scanner": "bg-indigo-100 text-indigo-600",
  "Atualização por drone": "bg-indigo-100 text-indigo-600",
  "Plantio de muda compensatória": "bg-green-100 text-green-600",
  "Queda de árvore": "bg-red-100 text-red-600",
  "Replantio": "bg-green-100 text-green-600",
  "Solicitação de vistoria pelo cidadão": "bg-yellow-100 text-yellow-600",
  "Dano por tempestade": "bg-red-100 text-red-600",
  "Supressão autorizada": "bg-orange-100 text-orange-600",
  "Interferência registrada": "bg-purple-100 text-purple-600",
  "Transplante autorizado": "bg-orange-100 text-orange-600",
};

const HistoryVersioningSection: FC = () => {
  const [showObjectPanel, setShowObjectPanel] = useState(false);

  return (
    <div className="space-y-6">
      {/* Timeline de eventos */}
      <div className="bg-white rounded-card border border-background-200/70 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground-900 font-heading">Linha do Tempo de Eventos Versionados</h3>
          <button
            onClick={() => setShowObjectPanel(!showObjectPanel)}
            className="text-xs font-medium text-blue-600 hover:text-blue-700 cursor-pointer whitespace-nowrap"
          >
            {showObjectPanel ? "Ocultar" : "Ver"} Objeto Versionado
          </button>
        </div>

        <div className="relative pl-8 space-y-0">
          <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-background-200"></div>
          {ladmEvents.slice(0, 10).map((evt) => (
            <div key={evt.id} className="relative pb-4 last:pb-0">
              <div className={`absolute left-[-23px] top-1 w-4 h-4 rounded-full border-2 border-white ${eventIcons[evt.evento] ? "bg-blue-500" : "bg-foreground-300"}`}></div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                <span className="text-xs font-mono text-foreground-400 whitespace-nowrap">{new Date(evt.data + "T00:00:00").toLocaleDateString("pt-BR")}</span>
                <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${eventColors[evt.evento] || "bg-gray-100 text-gray-600"}`}>
                  <i className={`${eventIcons[evt.evento] || "ri-record-circle-line"} text-xs`}></i>
                  {evt.evento}
                </span>
                <span className="text-xs font-mono text-foreground-700">{evt.codigoCta}</span>
                <span className="text-xs text-foreground-500">{evt.responsavel}</span>
                <span className="text-xs text-foreground-400 hidden sm:inline">
                  {evt.statusAnterior} <i className="ri-arrow-right-line mx-1"></i> {evt.statusNovo}
                </span>
              </div>
            </div>
          ))}
          {ladmEvents.length > 10 && (
            <p className="text-xs text-foreground-400 pt-2 ml-0 pl-0 relative z-10">+ {ladmEvents.length - 10} eventos adicionais</p>
          )}
        </div>
      </div>

      {/* Objeto Versionado */}
      {showObjectPanel && (
        <div className="bg-white rounded-card border border-background-200/70 p-5">
          <h3 className="text-sm font-semibold text-foreground-900 font-heading mb-4">
            Objeto Versionado: {versionedObjectExample.codigoCta} — {versionedObjectExample.especie}
            <span className="text-xs font-normal text-foreground-400 ml-2">({versionedObjectExample.nomeCientifico})</span>
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-100/70">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-foreground-600 font-heading">Campo</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-foreground-600 font-heading">Anterior</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-foreground-600 font-heading">Novo</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-foreground-600 font-heading">Fonte</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-foreground-600 font-heading">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/50">
                {versionedObjectExample.historico.map((h, i) => (
                  <tr key={i} className="hover:bg-background-50/60">
                    <td className="px-4 py-2.5 text-xs font-medium text-foreground-900">{h.campo}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground-500">{h.anterior}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground-700 font-semibold">{h.novo}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground-600 max-w-[200px] truncate">{h.fonte}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground-500 whitespace-nowrap">{new Date(h.data + "T00:00:00").toLocaleDateString("pt-BR")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryVersioningSection;